import { Flex } from "@radix-ui/themes";
import { useEffect, useState } from "react";

interface Option {
  answerId: number;
  value: string;
  isAnswer: boolean | number;
  weight?: number;
}

interface RankOrderQuestionProps {
  question: string;
  element: any;
  index: number;
  options: Option[];
  isMandatory?: boolean;
  onWeight: (wieght: number, option: Option, index: number) => void;
  onValidate?: (index: number, isValid: boolean) => void;
}

const WeightedQuestion: React.FC<RankOrderQuestionProps> = ({
  element,
  index,
  question,
  options,
  onWeight,
  onValidate,
  isMandatory
}) => {

  const [testState, setTestState] = useState<Record<number, number>>(() => {
    const initialState: Record<number, number> = {};

    options.forEach((option) => {
      if (typeof option.weight === "number") {
        initialState[option.answerId] = option.weight;
      }
    });

    return initialState;
  });
  const [error, setError] = useState<string | null>(null);
  const [totalWeightNew, setTotalWeightNew] = useState(Number(element.totalWeight));

  const calculateTotalWeight = () => {
    return options.reduce((sum, opt) => sum + (opt.weight || 0), 0);
  };

  const updateSelection = (weight: number, option: Option) => {
    // Validate individual input
    if (weight > totalWeightNew) {
      setError(`Individual weight must not exceed ${totalWeightNew}`);
      onValidate(index, false);
      return;
    }

    const temp = { ...testState };
    temp[option.answerId] = weight;
    setTestState(temp);
    onWeight(weight, option, index);

    const totalWeight = calculateTotalWeight() + weight - (testState[option.answerId] || 0);
    const isValid = totalWeight === totalWeightNew;
    setError(totalWeight === totalWeightNew ? null : `Total weight must equal ${totalWeightNew}`);
    onValidate(index, isValid);
  };

  useEffect(() => {
    if (typeof onValidate !== "function") return;
    const totalWeight = calculateTotalWeight();
    const isValid = totalWeight === totalWeightNew;
    setError(totalWeight === totalWeightNew ? null : `Total weight must equal ${totalWeightNew}`);
    onValidate(index, isValid);
  }, [testState, totalWeightNew, index]);

  return (
    <Flex direction="column" gap="3" style={{ maxWidth: 700 }}>
      <label className="block mb-2 font-bold text-app-gray-medium">
        {question}
        {isMandatory && <span className="text-red-600">*</span>}
      </label>

      {options.map((item) => (
        <Flex key={item.answerId} justify="between" gap="2">
          <div className="w-full break-words shadow-sm border rounded-xl px-2 py-2">
            {item.value}
          </div>
          <input
            type="number"
            className="border shadow-sm focus:outline-purple-700 px-1 py-1 rounded-xl w-32"
            onChange={(e) => {
              const inputValue = e.target.value;
              const weight = parseInt(inputValue);

              if (inputValue === "") {
                updateSelection(0, item);
              } else if (!isNaN(weight)) {
                updateSelection(weight, item);
              } else {
                setError("Please enter a valid number");
                onValidate(index, false);
              }
            }}
            value={testState[item.answerId] ?? ""}
            min="0"
          />
        </Flex>
      ))}
      {error && <div className="text-red-600">{error}</div>}
      <div className="flex w-full justify-end">
        <div className="w-32 text-center">
          <label>Total Weight</label>
          <div className="shadow-sm px-2 py-2 w-32 border rounded-xl bg-gradient-to-br from-purple-950 to-purple-500 text-white">
            {totalWeightNew}
          </div>
        </div>
      </div>
    </Flex>
  );
};

export default WeightedQuestion;
